import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode, useId, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import EmailRecipientsInput, {
  createRecipientsSchema,
} from '~components/FormFields/v2/EmailRecipientsInput';
import Label from '~components/FormFields/v2/Label';
import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { OutboundEmailDraft } from '~hooks/useOutboundEmails/models';
import { OutboundEmailEntry } from '~hooks/useOutboundEmails/sendOutboundEmails';
import {
  createEmailSubjectSchema,
  EMAIL_SUBJECT_MAX_LENGTH,
} from '~schemas/emailSubjectSchema';

import EmailDocumentDraftsStatus, {
  EmailDocumentDraftsLoadState,
} from './EmailDocumentDraftsStatus';

const NO_RECIPIENT_PARTY_PLACEHOLDER = '—';

const getLoadState = (
  isError: boolean,
  isLoading: boolean,
): EmailDocumentDraftsLoadState => {
  if (isError) {
    return 'error';
  }

  return isLoading ? 'loading' : 'empty';
};

interface EmailDocumentsDialogLabels {
  description: string;
  noEmails: string;
  send: string;
  title: string;
}

interface EmailDocumentsFormRow {
  defaultRecipients: string[];
  defaultSubject: string;
  ids: string[];
  message: string;
  recipients: string[];
  subject: string;
}

interface EmailDocumentsFormData {
  rows: EmailDocumentsFormRow[];
}

interface EmailDocumentsDialogProps {
  drafts: OutboundEmailDraft[] | null;
  filesFooterContent?: (state: { isSubmitting: boolean }) => ReactNode;
  headerContent?: (state: { isSubmitting: boolean }) => ReactNode;
  isError: boolean;
  isLoading: boolean;
  labels: EmailDocumentsDialogLabels;
  onClose: () => void;
  onRetry: () => void;
  onSend: (entries: OutboundEmailEntry[]) => Promise<void>;
}

const createEmailDocumentsSchema = () => {
  const recipientsSchema = createRecipientsSchema(t('email_documents.recipients_label'));
  const subjectLabel = t('email_documents.subject_label');

  return yup.object({
    rows: yup
      .array()
      .of(
        yup.object({
          defaultRecipients: yup.array().of(yup.string().defined()).defined(),
          defaultSubject: yup.string().defined(),
          ids: yup.array().of(yup.string().defined()).defined(),
          message: yup.string().trim().defined(),
          recipients: yup
            .array()
            .of(yup.string().defined())
            .defined()
            .when('defaultRecipients', ([defaultRecipients]: string[][], schema) =>
              defaultRecipients.length ? schema : recipientsSchema,
            ),
          subject: createEmailSubjectSchema(subjectLabel).when(
            'defaultSubject',
            ([defaultSubject]: string[], schema) =>
              defaultSubject
                ? schema
                : schema.required(
                    t('form_validation_errors.required', {
                      field: subjectLabel,
                    }),
                  ),
          ),
        }),
      )
      .defined(),
  });
};

// Matches a small outlined input's text so a read-only value lines up with the input
// beside it: AppTheme's `MuiInputBase` override sets 12px text, MUI's `InputBase` uses a
// 1.4375em line height, MUI pads small outlined input text 8.5px vertically, and
// AppTheme's `MuiTextField` override adds a 2px top margin.
const INPUT_ALIGNED_TEXT_SX = {
  color: 'text.primary',
  fontSize: '12px',
  lineHeight: '1.4375em',
  overflowWrap: 'anywhere',
} as const;
const INPUT_ALIGNED_BLOCK_SX = { mt: '2px', py: '8.5px' } as const;

interface EmailDocumentsRowsProps {
  drafts: OutboundEmailDraft[];
  filesFooterContent?: EmailDocumentsDialogProps['filesFooterContent'];
  formId: string;
  isSubmitting: boolean;
  onSubmit: (data: EmailDocumentsFormData) => Promise<void>;
}

function EmailDocumentsRows({
  drafts,
  filesFooterContent,
  formId,
  isSubmitting,
  onSubmit,
}: EmailDocumentsRowsProps) {
  const schema = useMemo(() => createEmailDocumentsSchema(), []);
  const { control, handleSubmit } = useForm<EmailDocumentsFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      rows: drafts.map(({ ids, message, recipients, subject }) => ({
        defaultRecipients: recipients,
        defaultSubject: subject,
        ids,
        message,
        recipients,
        subject,
      })),
    },
  });

  return (
    <Box
      autoComplete="off"
      component="form"
      id={formId}
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      {drafts.map((draft, index) => (
        <Box
          key={draft.ids.join(',')}
          sx={{
            alignItems: 'start',
            borderBottom: 1,
            borderColor: 'divider',
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(0, 1fr) minmax(0, 2fr)',
            },
            py: 2,
            rowGap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Label>{t('email_documents.company_label')}</Label>
            <Typography sx={{ ...INPUT_ALIGNED_TEXT_SX, ...INPUT_ALIGNED_BLOCK_SX }}>
              {draft.recipientParty?.name ?? NO_RECIPIENT_PARTY_PLACEHOLDER}
            </Typography>
          </Box>
          <EmailRecipientsInput
            control={control}
            isDisabled={isSubmitting}
            label={t('email_documents.recipients_label')}
            name={`rows.${index}.recipients`}
            placeholder={draft.recipients.join(', ') || undefined}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              gridRow: { sm: 'span 2' },
            }}
          >
            <Label>{t('email_documents.files_label')}</Label>
            <Stack sx={INPUT_ALIGNED_BLOCK_SX}>
              {draft.attachments.map(({ fileName, ticketCount }) => (
                <Typography key={fileName} sx={INPUT_ALIGNED_TEXT_SX}>
                  {fileName}
                  {ticketCount !== null && (
                    <Typography
                      component="span"
                      sx={{ ...INPUT_ALIGNED_TEXT_SX, color: 'text.secondary' }}
                    >
                      {' '}
                      {ticketCount > 0
                        ? t('email_documents.ticket_count', {
                            count: ticketCount,
                          })
                        : t('email_documents.no_tickets')}
                    </Typography>
                  )}
                </Typography>
              ))}
            </Stack>
            {filesFooterContent?.({ isSubmitting })}
          </Box>
          <TextInput
            control={control}
            isDisabled={isSubmitting}
            isRequired
            label={t('email_documents.subject_label')}
            name={`rows.${index}.subject`}
            placeholder={draft.subject || undefined}
            slotProps={{ htmlInput: { maxLength: EMAIL_SUBJECT_MAX_LENGTH } }}
          />
          <TextInput
            control={control}
            isDisabled={isSubmitting}
            label={t('email_documents.message_label')}
            maxRows={8}
            minRows={3}
            name={`rows.${index}.message`}
            placeholder={draft.message || undefined}
          />
        </Box>
      ))}
    </Box>
  );
}

export default function EmailDocumentsDialog({
  drafts,
  filesFooterContent,
  headerContent,
  isError,
  isLoading,
  labels,
  onClose,
  onRetry,
  onSend,
}: EmailDocumentsDialogProps) {
  const formId = useId();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async ({ rows }: EmailDocumentsFormData) => {
    setIsSubmitting(true);
    try {
      await onSend(
        rows.map(({ ids, message, recipients, subject }) => ({
          ids,
          ...(recipients.length ? { recipients } : {}),
          ...(subject ? { subject } : {}),
          ...(message ? { message } : {}),
        })),
      );
      onClose();
    } catch {
      // `onSend` reports its own failures; the dialog stays open so the user can retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    onClose();
  };

  return (
    <Dialog isOpen onClose={handleClose} maxWidth="md">
      <DialogHeader onClose={handleClose}>{labels.title}</DialogHeader>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {labels.description}
        </Typography>

        {headerContent?.({ isSubmitting })}

        {drafts?.length ? (
          <EmailDocumentsRows
            drafts={drafts}
            filesFooterContent={filesFooterContent}
            formId={formId}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />
        ) : (
          <EmailDocumentDraftsStatus
            noEmailsLabel={labels.noEmails}
            onRetry={onRetry}
            state={getLoadState(isError, isLoading)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSubmitting}
          onClick={handleClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>
        <SmallLoadingButton
          color="primary"
          disabled={!drafts?.length}
          form={formId}
          loading={isSubmitting}
          type="submit"
          variant="contained"
        >
          {labels.send}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
